import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminNewsletterForm() {
    const [emailLists, setEmailLists] = useState([]);
    const [selectedListId, setSelectedListId] = useState("");
    const [headerText, setHeaderText] = useState("JOIN OUR NEWSLETTER");
    const [descriptionText, setDescriptionText] = useState("Stay updated with the latest news and events from the Garden Club of the Eastern Shore");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const token = await auth.currentUser.getIdToken();
            
            // Fetch all email lists (not filtering by is_publicly_joinable)
            const emailListsRes = await axios.get("/api/emails", {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Show all email lists
            setEmailLists(emailListsRes.data);

            // Fetch current newsletter setting
            const newsletterRes = await axios.get("/api/public/newsletter", {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const currentListId = newsletterRes.data.content?.email_list_id || "";
            const currentHeader = newsletterRes.data.content?.header_text || "JOIN OUR NEWSLETTER";
            const currentDescription = newsletterRes.data.content?.description_text || "Stay updated with the latest news and events from the Garden Club of the Eastern Shore";
            
            setSelectedListId(currentListId);
            setHeaderText(currentHeader);
            setDescriptionText(currentDescription);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load newsletter settings");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            
            // First, set all email lists as not publicly joinable
            await axios.patch("/api/emails/reset-public-status", {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // If a list is selected, set it as publicly joinable
            if (selectedListId) {
                await axios.patch(`/api/emails/${selectedListId}/set-public`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            
            // Update the newsletter setting
            await axios.put("/api/public/newsletter", {
                content: {
                    email_list_id: selectedListId,
                    header_text: headerText,
                    description_text: descriptionText
                }
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success("Newsletter settings updated!");
        } catch (error) {
            console.error("Error saving newsletter settings:", error);
            toast.error("Failed to save newsletter settings");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div>Loading newsletter settings...</div>;
    }

    return (
        <div>
            <h4>Newsletter Form</h4>
            <p className="text-body-secondary">
                If an email list is selected, then a form will appear on the homepage where anybody can join the list.
            </p>
            
            <div className="mb-3">
                <label className="form-label">Select Email List</label>
                <select 
                    className="form-select"
                    value={selectedListId}
                    onChange={(e) => setSelectedListId(e.target.value)}
                >
                    <option value="">Disabled</option>
                    {emailLists.map((list) => (
                        <option key={list._id} value={list._id}>
                            {list.group_name} ({list.members.length} members)
                        </option>
                    ))}
                </select>
                {emailLists.length === 0 && (
                    <div className="form-text">
                        No email lists found. Create an email list first.
                    </div>
                )}
            </div>

            {/* Show custom text fields when a newsletter is selected */}
            {selectedListId && (
                <>
                    <div className="mb-3">
                        <label className="form-label">Header Text</label>
                        <input
                            type="text"
                            className="form-control"
                            value={headerText}
                            onChange={(e) => setHeaderText(e.target.value)}
                            placeholder="JOIN OUR NEWSLETTER"
                        />
                        <div className="form-text">
                            This text will appear as the main heading on the newsletter signup form.
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Description Text</label>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={descriptionText}
                            onChange={(e) => setDescriptionText(e.target.value)}
                            placeholder="Stay updated with the latest news and events from the Garden Club of the Eastern Shore"
                        />
                        <div className="form-text">
                            This description will appear below the header to explain what the newsletter is about.
                        </div>
                    </div>
                </>
            )}

            <button 
                className="btn btn-admin"
                onClick={handleSave}
                disabled={saving}
            >
                {saving ? "Saving..." : "Save"}
            </button>
        </div>
    );
}